import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { BackButton } from "../../../components/common/GeneralComponent";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/partials/Loader";
import { useDispatch, useSelector } from "react-redux";
import AdministrativeInfo from "./AdministrativeInfo";
import BeneficiaryDetails from "./BeneficiaryDetails";
import GrantDetails from "./GrantDetails";
import MilestonesProgress from "./MilestonesProgress";
import VerificationSupport from "./VerificationSupport";
import OverallProgress from "./OverallProgress";
import Attachments from "./Attachments";
import { getGrantTypeConfig } from "../grantTypeConfig";

const AddSmeGrant = ({ grantType = "sme" }) => {
  const cfg = getGrantTypeConfig(grantType);
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];

  const scrollRef = useRef(null);

  const [isLeftClicked, setIsLeftClicked] = useState(false);
  const [isRightClicked, setIsRightClicked] = useState(false);
  const [completedTabs, setCompletedTabs] = useState(
    new Set(["Administrative Info"])
  );
  const [grantId, setGrantId] = useState(null);
  const [grantData, setGrantData] = useState(null);

  const [formDataState, setFormDataState] = useState({
    administrativeInfo: {},
    beneficiaryDetails: {},
    grantDetails: {},
    milestonesProgress: [],
    verificationSupport: {},
    overallProgress: {},
  });

  const [sectionValidation, setSectionValidation] = useState({
    administrativeInfo: false,
    beneficiaryDetails: false,
    grantDetails: false,
    milestonesProgress: false,
    verificationSupport: false,
    overallProgress: false,
  });
  const allTabs = [
    {
      name: "Administrative Info",
      permission: `${cfg.permissionPrefix}-administrative-info-create`,
    },
    {
      name: "Beneficiary Details",
      permission: `${cfg.permissionPrefix}-beneficiary-details-create`,
    },
    { name: "Grant Details", permission: `${cfg.permissionPrefix}-grants-details-create` },
    {
      name: "Milestones Progress",
      permission: `${cfg.permissionPrefix}-milestone-progress-create`,
    },
    {
      name: "Verification & Support",
      permission: `${cfg.permissionPrefix}-verification-support-create`,
    },
    {
      name: "Overall Progress",
      permission: `${cfg.permissionPrefix}-overall-progress-create`,
    },
    {
      name: "Attachments",
      permission: `${cfg.attachmentPermissionPrefix}-create`,
    },
  ];
  const allowedTabs = useMemo(() => {
    return allTabs.filter((tab) => permissions.includes(tab.permission));
  }, [permissions]);
  const [activeTab, setActiveTab] = useState(allowedTabs[0]?.name || "");
  const updateGrantIdRef = useRef(null);
  useEffect(() => {
    updateGrantIdRef.current = (newGrantId) => setGrantId(newGrantId);
    window.updateGrantId = updateGrantIdRef.current;
    return () => {
      if (window.updateGrantId === updateGrantIdRef.current) {
        delete window.updateGrantId;
      }
      updateGrantIdRef.current = null;
    };
  }, []);
  useEffect(() => {
    return () => {
      setFormDataState({
        administrativeInfo: {},
        beneficiaryDetails: {},
        grantDetails: {},
        milestonesProgress: [],
        verificationSupport: {},
        overallProgress: {},
      });
      setSectionValidation({
        administrativeInfo: false,
        beneficiaryDetails: false,
        grantDetails: false,
        milestonesProgress: false,
        verificationSupport: false,
        overallProgress: false,
      });
    };
  }, []);
  const scrollTabs = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === "left" ? -200 : 200;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
      if (direction === "left") {
        setIsLeftClicked(true);
        setTimeout(() => setIsLeftClicked(false), 200);
      } else {
        setIsRightClicked(true);
        setTimeout(() => setIsRightClicked(false), 200);
      }
    }
  };
  const handleGrantCreated = useCallback((newGrantId, data) => {
    setGrantId(newGrantId);
    setGrantData(data);
  }, []);

  const storeFormData = useCallback((section, data) => {
    setFormDataState((prev) => ({
      ...prev,
      [section]: data,
    }));
  }, []);

  const handleSectionValidation = useCallback((section, isValid) => {
    setSectionValidation((prev) => ({
      ...prev,
      [section]: isValid,
    }));
  }, []);

  const handleFinalSubmit = useCallback(() => {
    navigate(`/${cfg.routeBase}`);
  }, [navigate, cfg.routeBase]);

  const handleNext = useCallback(() => {
    const currentIndex = allowedTabs.findIndex((t) => t.name === activeTab);
    if (currentIndex < allowedTabs.length - 1) {
      const nextTab = allowedTabs[currentIndex + 1].name;
      setCompletedTabs((prev) => new Set([...prev, nextTab]));
      setActiveTab(nextTab);
    }
  }, [activeTab, allowedTabs]);

  const handleTabClick = useCallback(
    (tabName) => {
      if (completedTabs.has(tabName) || tabName === activeTab) {
        setActiveTab(tabName);
      }
    },
    [completedTabs, activeTab]
  );

  const isTabAccessible = useCallback(
    (tabName) => completedTabs.has(tabName) || tabName === activeTab,
    [completedTabs, activeTab]
  );

  const storeAdministrativeInfo = useCallback(
    (data) => storeFormData("administrativeInfo", data),
    [storeFormData]
  );
  const storeBeneficiaryDetails = useCallback(
    (data) => storeFormData("beneficiaryDetails", data),
    [storeFormData]
  );
  const storeGrantDetails = useCallback(
    (data) => storeFormData("grantDetails", data),
    [storeFormData]
  );
  const storeMilestonesProgress = useCallback(
    (data) => storeFormData("milestonesProgress", data),
    [storeFormData]
  );
  const storeVerificationSupport = useCallback(
    (data) => storeFormData("verificationSupport", data),
    [storeFormData]
  );
  const storeOverallProgress = useCallback(
    (data) => storeFormData("overallProgress", data),
    [storeFormData]
  );

  const handleAdministrativeValidation = useCallback(
    (isValid) => handleSectionValidation("administrativeInfo", isValid),
    [handleSectionValidation]
  );
  const handleBeneficiaryValidation = useCallback(
    (isValid) => handleSectionValidation("beneficiaryDetails", isValid),
    [handleSectionValidation]
  );
  const handleGrantValidation = useCallback(
    (isValid) => handleSectionValidation("grantDetails", isValid),
    [handleSectionValidation]
  );
  const handleMilestonesValidation = useCallback(
    (isValid) => handleSectionValidation("milestonesProgress", isValid),
    [handleSectionValidation]
  );
  const handleVerificationValidation = useCallback(
    (isValid) => handleSectionValidation("verificationSupport", isValid),
    [handleSectionValidation]
  );
  const handleOverallValidation = useCallback(
    (isValid) => handleSectionValidation("overallProgress", isValid),
    [handleSectionValidation]
  );

  // if (allowedTabs.length === 0) {
  //   return (
  //     <div className="p-6 text-center text-gray-500">
  //       You do not have permission to access/create any grant sections.
  //     </div>
  //   );
  // }

  return (
    <div className="w-full my-4 mx-auto">
      {loader && <Loader />}

      <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Create {cfg.label}
        </h1>
        <BackButton text="Back" to={`/${cfg.routeBase}`} />
      </div>

      <div className="mt-6">
        <div className="flex items-center rounded-t-lg bg-[#fdf5ec] relative">
          <button
            onClick={() => scrollTabs("left")}
            className={`text-xl px-4 py-2 font-bold rounded-lg transition-colors ${
              isLeftClicked ? "bg-[#caa316] text-white" : "text-black"
            }`}
          >
            «
          </button>

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-hidden scrollbar-hide"
          >
            {allowedTabs.map((tab) => {
              const isAccessible = isTabAccessible(tab.name);
              return (
                <button
                  key={tab.name}
                  onClick={() => handleTabClick(tab.name)}
                  disabled={!isAccessible}
                  className={`px-6 py-2 font-semibold whitespace-nowrap transition-colors ${
                    activeTab === tab.name
                      ? "bg-[#caa316] text-white"
                      : isAccessible
                      ? "text-black hover:bg-gray-100"
                      : "text-gray-400 cursor-not-allowed opacity-50"
                  }`}
                  style={{
                    borderTopLeftRadius: "13px",
                    borderTopRightRadius: "30px",
                  }}
                  title={
                    !isAccessible
                      ? "Complete previous sections to access this tab"
                      : ""
                  }
                >
                  {tab.name}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => scrollTabs("right")}
            className={`ml-auto text-xl px-4 py-2 rounded-lg font-bold transition-colors ${
              isRightClicked ? "bg-[#caa316] text-white" : "text-black"
            }`}
          >
            »
          </button>
        </div>

        <div className="bg-white rounded-b-lg shadow-md p-6">
          {activeTab === "Administrative Info" && (
            <AdministrativeInfo
              grantType={grantType}
              onNext={handleNext}
              isEditMode={false}
              showNextOnly={true}
              grantId={grantId}
              onGrantCreated={handleGrantCreated}
              onStoreFormData={storeAdministrativeInfo}
              onValidationChange={handleAdministrativeValidation}
              initialData={formDataState.administrativeInfo}
            />
          )}

          {activeTab === "Beneficiary Details" && (
            <BeneficiaryDetails
              grantType={grantType}
              grantId={grantId}
              onNext={handleNext}
              isEditMode={false}
              showNextOnly={true}
              onStoreFormData={storeBeneficiaryDetails}
              onValidationChange={handleBeneficiaryValidation}
              initialData={formDataState.beneficiaryDetails}
            />
          )}

          {activeTab === "Grant Details" && (
            <GrantDetails
              grantType={grantType}
              grantId={grantId}
              onNext={handleNext}
              isEditMode={false}
              showNextOnly={true}
              onStoreFormData={storeGrantDetails}
              onValidationChange={handleGrantValidation}
              initialData={formDataState.grantDetails}
            />
          )}

          {activeTab === "Milestones Progress" && (
            <MilestonesProgress
              grantType={grantType}
              grantId={grantId}
              onNext={handleNext}
              isEditMode={false}
              showNextOnly={true}
              onStoreFormData={storeMilestonesProgress}
              onValidationChange={handleMilestonesValidation}
              initialData={formDataState.milestonesProgress}
              approvedAmountPkr={
                formDataState.grantDetails.approved_grant_amount_pkr ||
                grantData?.approved_grant_amount_pkr
              }
              approvedAmountEuro={
                formDataState.grantDetails.approved_grant_amount_euro ||
                grantData?.approved_grant_amount_euro
              }
              includeSevenPercentAdvance={grantData?.include_seven_percent_advance}
              sevenPercentAdjustMilestone={grantData?.seven_percent_adjust_milestone}
            />
          )}

          {activeTab === "Verification & Support" && (
            <VerificationSupport
              grantType={grantType}
              grantId={grantId}
              onNext={handleNext}
              isEditMode={false}
              showNextOnly={true}
              onStoreFormData={storeVerificationSupport}
              onValidationChange={handleVerificationValidation}
              initialData={formDataState.verificationSupport}
            />
          )}

          {activeTab === "Overall Progress" && (
            <OverallProgress
              grantType={grantType}
              grantId={grantId}
              onNext={handleNext}
              isEditMode={false}
              showNextOnly={true}
              onStoreFormData={storeOverallProgress}
              onValidationChange={handleOverallValidation}
              initialData={formDataState.overallProgress}
            />
          )}

          {activeTab === "Attachments" && (
            <Attachments
              grantType={grantType}
              grantId={grantId}
              onSectionSaved={handleFinalSubmit}
              isEditMode={false}
              showFinalSubmit={true}
              allFormData={formDataState}
              sectionValidation={sectionValidation}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AddSmeGrant;